/**
 * FunctionHeader - Displays function name, pillar badge, and description
 *
 * Used at the top of assessment modals and panels to identify the
 * current function being assessed.
 */

import React from 'react';
import { ChevronLeft, Info, ExternalLink } from 'lucide-react';
import MaturityLevelBadge from './MaturityLevelBadge';
import { CISA_PILLARS } from '../../config/cisaMaturityModel';
import './FunctionHeader.css';

export default function FunctionHeader({
  functionCode,
  functionName,
  pillarCode,
  description,
  currentLevel,
  targetLevel,
  isNew = false,
  onBack,
  onInfoClick,
  animated = true
}) {
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);

  return (
    <header className={`fh-header ${animated ? 'fh-header--animated' : ''}`}>
      <div className="fh-top-row">
        {onBack && (
          <button
            className="fh-back-btn"
            onClick={onBack}
            aria-label="Go back"
          >
            <ChevronLeft size={20} />
          </button>
        )}

        <div className="fh-pillar-badge" style={{ '--pillar-color': pillar?.color }}>
          {pillar?.name || pillarCode}
        </div>

        {isNew && (
          <span className="fh-new-badge">New in v2.0</span>
        )}

        <div className="fh-spacer" />

        {onInfoClick && (
          <button
            className="fh-info-btn"
            onClick={onInfoClick}
            aria-label="More information"
          >
            <Info size={18} />
          </button>
        )}
      </div>

      <div className="fh-main">
        <h2 className="fh-title">{functionName}</h2>
        <code className="fh-code">{functionCode}</code>
      </div>

      {description && (
        <p className="fh-description">{description}</p>
      )}

      {(currentLevel || targetLevel) && (
        <div className="fh-levels">
          {currentLevel && (
            <div className="fh-level-item">
              <span className="fh-level-label">Current</span>
              <MaturityLevelBadge level={currentLevel} size="md" animated={animated} />
            </div>
          )}
          {currentLevel && targetLevel && (
            <span className="fh-level-arrow">→</span>
          )}
          {targetLevel && (
            <div className="fh-level-item">
              <span className="fh-level-label">Target</span>
              <MaturityLevelBadge level={targetLevel} size="md" variant="outline" animated={animated} />
            </div>
          )}
        </div>
      )}
    </header>
  );
}

/**
 * FunctionHeaderCompact - Minimal version for constrained spaces
 */
export function FunctionHeaderCompact({
  functionCode,
  functionName,
  pillarCode,
  currentLevel,
  onClick,
  animated = true
}) {
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);

  return (
    <button
      className={`fh-compact ${animated ? 'fh-compact--animated' : ''}`}
      onClick={onClick}
      style={{ '--pillar-color': pillar?.color }}
    >
      <div className="fh-compact-left">
        <span className="fh-compact-pillar">{pillar?.name}</span>
        <span className="fh-compact-name">{functionName}</span>
      </div>
      {currentLevel && (
        <MaturityLevelBadge level={currentLevel} size="sm" />
      )}
    </button>
  );
}

/**
 * FunctionBreadcrumb - Breadcrumb-style function identifier
 */
export function FunctionBreadcrumb({
  pillarName,
  functionName,
  onPillarClick,
  animated = true
}) {
  return (
    <nav className={`fh-breadcrumb ${animated ? 'fh-breadcrumb--animated' : ''}`}>
      <button className="fh-breadcrumb-item" onClick={onPillarClick}>
        {pillarName}
      </button>
      <span className="fh-breadcrumb-sep">/</span>
      <span className="fh-breadcrumb-current">{functionName}</span>
    </nav>
  );
}
